import React, { useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { useLocation } from "react-router-dom";
import Header from "../components/layout/Header";
import SideBar from "../components/layout/SideBar";
import ScatterPlot from "../components/layout/ScatterPlot"
import SearchBar from "../components/search/SearchBar"
import ScrollableBox from "../components/widget/ScrollableBox"
import SearchSuggestion from "../components/search/SearchSuggestion"
import dataset_specific_data from '../data/Data';
import ScrollableInfoPopup from '../components/infoview/ScrollableInfoPopup';

import axios from 'axios';
import Swal from 'sweetalert2';

import * as searchSuggestion from '../hooks/search/searchSuggestion';
import * as keyBrowse from '../hooks/search/keyBrowse';
import * as search from '../hooks/search/search';

const ContentPage = () => {
    const location = useLocation();
    const args = new URLSearchParams(location.search);
    const num_search_suggestions = 10

    const n_components = args.get("dimension");
    const method = args.get("method");
    const dataset = args.get("dataset");
    const perplexity = args.get("perplexity");
    const n_neighbours = args.get("n_neighbours");
    const min_dist = args.get("min_dist");
    const collection = args.get("coll");

    const [data, setData] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(null);
    const [groupingLabel, setGroupingLabel] = useState(dataset_specific_data[dataset]["default_label"]);
    const [searchValue, setSearchValue] = useState("");
    const [searchType, setSearchType] = useState(null);
    const [searchInstanceID, setSearchInstanceID] = useState(null);
    const [sidebarOpen, setSideBarOpen] = useState(false);
    const [toggleAttributes, setToggleAttributes] = useState(null);
    const [numHighlighted, setNumHighlighted] = useState(0);

    const [searchResults, setSearchResults] = useState([]);

    const [popupData, setPopupData] = useState(null);

    const clear_search = () => {
        //set grouping_label after clearing search
        if (!toggleAttributes.includes(groupingLabel)) {
            setGroupingLabel(dataset_specific_data[dataset]["default_label"])
        }
        search.clear_search(setSearchValue, setSearchType)
    }

    const show_clear_btn = (id) => {
        var clear_btn = document.getElementById((id + "_clear"));
        clear_btn.style.display = "flex";
    }
    
    const get_data = (
        max_entries_per_class="-1", 
        _groupingLabel = null, 
        cluster_algorithm="-1", 
        cluster_config={},
        hiddenToggleAttributes={}) => {
    
        setDataLoaded(null)
        _groupingLabel = _groupingLabel ? _groupingLabel : groupingLabel

        let params = {
            n_components: n_components,
            model: method,
            dataset: dataset,
            perplexity: perplexity,
            n_neighbours: n_neighbours,
            min_dist: min_dist,
            max_entries_per_class: max_entries_per_class,
            groupingLabel: _groupingLabel,
            cluster_algorithm: cluster_algorithm,
            coll: collection,
        };
    
        if (Object.keys(cluster_config).length !== 0) {
            Object.keys(cluster_config).forEach((config) => {
                params[config] = cluster_config[config]
            });
        }

        if (Object.keys(hiddenToggleAttributes).length !== 0) {
            Object.keys(hiddenToggleAttributes).forEach((attribute) => {
                hiddenToggleAttributes[attribute].forEach(val => (
                    params[("hiddenAttributes_" + attribute + ":::" + val)] = 1
                ))
            });
        }

    
        axios.get('/api/get_data', { params })
        .then(response => {
            setData(response.data);
            setToggleAttributes(Object.keys(response.data.metadata["toggleAttributes"]))
            setGroupingLabel(_groupingLabel)
            if (max_entries_per_class != "-1") {
                show_clear_btn("entries_per_class")
            }
        })
        .catch(error => {
            console.error('There was an error fetching the data!', error);
    
            let msg = error.response.data.error
            Swal.fire({
                icon: 'error',
                title: 'Error!.',
                text: msg,
            });
        })
        .finally(() => {
            setDataLoaded(true)
        });
    }

    const get_by_id = (id) => {
        let params = {
            id: id,
            dataset: dataset,
            n_components: n_components,
            model: method,
            perplexity: perplexity,
            n_neighbours: n_neighbours,
            min_dist: min_dist,
            coll: collection
        };

        axios.get('/api/get_by_id', { params })
        .then(response => {
            setPopupData(response.data);
        })
        .catch(error => {
            console.error('There was an error when fetching data by id!', error);
    
            let msg = error.response.data.error
            Swal.fire({
                icon: 'error',
                title: 'Error!.',
                text: msg,
            });
        })
        .finally(() => {
        });
    }
    
    //STAY OR GO?
    
    const toggleSideBar = () => {
        setSideBarOpen(!sidebarOpen);
    };

    //SCATTER PLOT - STAY

    const handleSearchLoading = async () => {
        await search.handleSearch(data, setSearchValue, setSearchType, setGroupingLabel, setSearchInstanceID, setDataLoaded);

    };

    const scatterPlot = React.useMemo(() => {
        if (data && dataLoaded) {
            return (
                <ScatterPlot 
                    data={data["data"]}
                    grouping_label={groupingLabel} 
                    is3D={n_components === "3"}
                    searchValue={searchValue}
                    searchType={searchType}
                    instanceID={searchInstanceID}
                    get_by_id={get_by_id}
                    setNumHighlighted = {setNumHighlighted}
                />
            );
        } else {
            return <div className='centered scatter-spinner-container'><Spinner animation="border" variant="primary" className='scatter-spinner large'/></div>;
        }
    }, [data, dataLoaded, n_components, groupingLabel, searchValue]);


    useEffect(() => {
        get_data(-1);
        window.addEventListener('keydown', keyBrowse.handleKeyDown);
        window.addEventListener('keyup', keyBrowse.handleKeyUp);
        window.addEventListener('keypress', keyBrowse.handleKeyEnter);
    }, []);

    const get_data_length = () => {
        if (data["data"] && data["data"] !== null) {
            return data["data"].length
        } else {
            return 0
        }
    }

    

    return (
        <div>
            {data ? <SideBar isOpen={sidebarOpen} get_data={get_data} method={method} coll_id={collection} n_components={n_components} setGroupingLabel={setGroupingLabel} metadata={data.metadata} data_length={get_data_length()} numHighlighted={numHighlighted}/> : ""}
            <div className="flex-column fill-page">
                <Header sidebarOpen={sidebarOpen} toggleSideBar={toggleSideBar} />
                <div className='flex-column search-bar-container' style={{gap: "12px"}}>
                    <SearchBar handleSearch={handleSearchLoading} clearAction={() => clear_search()} onChange={(e) => searchSuggestion.updateInputSuggestions(e, data, setSearchResults, num_search_suggestions)} />
                    <ScrollableBox id="search_suggestions" track_color='#b4b4b4' thumb_color='#333436' thumb_hover_color='#54565a' width='324px' maxHeight='200px'>
                        {searchResults.map((result, index) => (
                            <SearchSuggestion 
                                key={index} 
                                content={result.result} 
                                type={result.attribute} 
                                hover_color="#b4b4b4"
                                id={result.isInstance ? result.id : null}
                                onClick={() => search.updateSearchBar(result.result)}
                            />
                        ))}
                        
                    </ScrollableBox>          
                </div>
                <div id="scatter_seperator" style={{height: "70px"}}></div>
                {scatterPlot}
                {<ScrollableInfoPopup content={popupData}/>}
            </div>
        </div>
    );
};

export default ContentPage;